import { useFormikContext } from "formik";
import parse from 'html-react-parser';
import PackageInterface from "../../Interfaces/PackageInterface";
import ConfiguratorValue from "../../Interfaces/ConfiguratorValue";
import { formatMoney, getImageSizes, getSrcSet, sectionSettings, translations } from "../../Helpers/WindowHelpers";
import { updateUpsellPackages } from "../../Helpers/FormikUpdateFunctions";

interface UpsellPackageModalBodyDisplayInterface {
    upsellPackage: PackageInterface,
    step: number,
    closeModal?: () => void,
}

const getBaseTotalSum = (upsellPackage: PackageInterface) =>
    upsellPackage.variants.map((variant) => parseInt(variant.price)).reduce((sum, amount) => sum + amount, 0);

const getRoundedTotalSum = (upsellPackage: PackageInterface) =>
    upsellPackage.variants.map((variant) => parseInt(variant.price)).reduce((sum, amount) => sum + Math.round(amount / 100), 0);

const getFinalTotal = (upsellPackage: PackageInterface) => {
    const baseTotalSum = getBaseTotalSum(upsellPackage);
    if (upsellPackage.discount_amount <= 0) return baseTotalSum;

    const roundedTotalSum = getRoundedTotalSum(upsellPackage);
    return upsellPackage.discount_type == "Percentage"
        ? (roundedTotalSum - (roundedTotalSum * (upsellPackage.discount_amount / 100))) * 100
        : roundedTotalSum - upsellPackage.discount_amount;
}

const UpsellPackageModalBodyDisplay = ({ upsellPackage, step, closeModal }: UpsellPackageModalBodyDisplayInterface) => {
    const context = useFormikContext<any>();
    const baseTotalSum = getBaseTotalSum(upsellPackage);
    const finalTotal = getFinalTotal(upsellPackage);
    const isSelected = context.values?.variants?.some((item: ConfiguratorValue) => item.value == upsellPackage.name);

    const priceRender = () => {
        if (finalTotal <= 0) return (<>{sectionSettings.label_free}</>);
        return (<>+{formatMoney(finalTotal.toString())}</>);
    }

    const compareAtPriceRender = () =>
        baseTotalSum > finalTotal ? (<span className="text--strike color--gray-muted">{formatMoney(baseTotalSum.toString())}</span>) : null;

    const submit = () => {
        if (!isSelected) {
            updateUpsellPackages(upsellPackage, finalTotal.toString(), context, step);
        }
        if (typeof closeModal !== 'undefined') {
            closeModal();
        }
    }

    return (
        <div className="d--flex flex-column gap-30">
            {upsellPackage.image.src &&
                <div className="ratio ratio--16-9 round width--100 mx-auto details-modal-image">
                    <span className="visually-hidden">{upsellPackage.image.alt}</span>
                    <img
                        className="d--block ratio__content ratio__content--cover"
                        srcSet={getSrcSet(upsellPackage.image.src, [350, 525, 700, 1050])}
                        sizes={getImageSizes('525px', [
                            {
                                screen: 767,
                                view: '350px'
                            }
                        ])}
                        alt={upsellPackage.image.alt}
                        loading="lazy"
                    />
                </div>
            }
            <div className="d--flex flex-column gap-8 justify--center width--100">
                <h3 className="h4 title">{upsellPackage.name}</h3>
                <div className="modal-text mt-10">{parse(upsellPackage.modal_description ?? '')}</div>
                <div className="d--flex justify--end gap-20 align-items--center">
                    <p className="promo-cards__price mb-0">
                        <span className="d--block width--100 text--sm text--bold percent--off">
                            {upsellPackage.discount_amount}{upsellPackage.discount_type == "Percentage" ? `%${sectionSettings.label_off}` : `${sectionSettings.label_off_each}`}
                        </span>
                        {priceRender()} {compareAtPriceRender()}
                    </p>
                    <button type="button" className="details-modal-add button button--primary " onClick={submit}>
                        {translations.quick_add}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default UpsellPackageModalBodyDisplay;
